import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { candidateIdFromPath, quickBgvTo } from './quickBgvLink';

// ===========================================================================
// "Quick BGV" — the Actions-menu item on the candidate page
// ===========================================================================
//
// It sits directly below "Background Verify" in the Actions dropdown on
// /ats/candidates/:id and does one thing: take this candidate to the BGV
// screen with the BGV screen already knowing who they are.
//
// THREE THINGS THIS COMPONENT DELIBERATELY DOES NOT DO
// ----------------------------------------------------
//
// 1. IT DOES NOT OPEN A CASE. Opening one emails the candidate a document
//    request and starts a 48-hour clock, and routes/bgv_intake.py reserves
//    that for an admin. A recruiter clicking this gets the same screen with a
//    "Send to BGV" button instead, because the BGV page already knows the
//    signed-in role and the server already refuses the wrong one. Deciding
//    here as well would be a second copy of that rule, free to disagree with
//    the first.
//
// 2. IT DOES NOT IMPORT ANYTHING FROM THE CANDIDATE PAGE. It is dropped into a
//    file this build has never seen, by a script, so it takes no props it
//    cannot do without and reads the candidate id from the URL — which the
//    route /ats/candidates/:id guarantees. `candidate` is optional enrichment;
//    without it the far end simply asks the server instead.
//
// 3. IT DOES NOT DECIDE WHAT IT LOOKS LIKE. `as` is the element the host menu
//    uses for its own items, passed in by the wiring script after reading the
//    "Background Verify" item next to it. That way the new row inherits the
//    menu's keyboard handling, close-on-select and styling, whether this menu
//    is Radix's DropdownMenuItem or a hand-rolled <button>. Rendering our own
//    button inside a Radix menu would look right and behave wrong.

export default function QuickBgvMenuItem({
  as: As = 'button',
  candidate = null,
  className,
  children,
  onNavigate,
  ...rest
}) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // useParams() is correct on both routes that render the candidate page
  // (/ats/candidates/:id and the public /candidate/:id, which share the :id
  // name). The path read is the fallback for any route added later, and the
  // handoff object is the fallback for both.
  const candidateId =
    (candidate && (candidate.id || candidate._id)) ||
    params.id ||
    params.candidateId ||
    candidateIdFromPath(location.pathname);

  const go = useCallback((event) => {
    // A menu item may be a <button> inside a <form> on some pages; a stray
    // submit would reload the app and lose the handoff.
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    const { to, options } = quickBgvTo(candidateId, candidate);
    if (!candidateId) {
      // Cannot happen on the two routes that render this page, but a menu item
      // that silently does nothing is the worst possible outcome, so say so.
      // eslint-disable-next-line no-alert
      window.alert(
        'Quick BGV could not tell which candidate this is. Open the candidate '
        + 'from the Candidates list and try again.',
      );
      return;
    }
    navigate(to, options);
    if (onNavigate) onNavigate();
  }, [candidate, candidateId, navigate, onNavigate]);

  // `type` is only meaningful on a real <button>. Radix's DropdownMenuItem
  // renders a div, and handing a div a type attribute is invalid markup for no
  // benefit.
  const typeProp = As === 'button' ? { type: 'button' } : {};

  return (
    <As
      {...typeProp}
      className={className}
      onClick={go}
      data-testid="quick-bgv-menu-item"
      {...rest}
    >
      <ShieldCheck className="w-4 h-4 mr-2" />
      {children || 'Quick BGV'}
    </As>
  );
}
